import { ColorPicker, Grid, GridColumn, GridRow, Input, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import type { BpPermissionChecker } from 'utils/PermissionChecker';
import * as Yup from 'yup';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../hooks/useCreateSelectOptions';
import { type SubjectType } from '../../pages/Institution/subpages/InstitutionSubjectsSubpage';
import { colorPickerOptions, UsedColors } from '../../utils/colorHelper';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import styles from './SubjectForm.module.scss';

const CreateSubjectMutationDocument = graphql(`
  mutation CreateBpSubject($input: BpSubjectCreateInput!) {
    createBpSubject(input: $input) {
      uuid
      __typename
    }
  }
`);
const UpdateSubjectMutationDocument = graphql(`
  mutation UpdateBpSubject($update: BpSubjectUpdateInput!, $uuid: ID!) {
    updateBpSubject(update: $update, uuid: $uuid) {
      uuid
      __typename
      globalSubject
      name
      shortName
      color
    }
  }
`);

const SubjectFormPageData = graphql(`
  query SubjectFormPageData {
    globalSubjects(where: { country: "de" }) {
      __typename
      subject
    }
  }
`);

type SubjectFormProps = {
  closeModal: () => void;
  subject?: SubjectType | undefined;
  perms: BpPermissionChecker | null;
  colorsInUse: Array<UsedColors | null>;
};

export const SubjectForm = ({ colorsInUse, closeModal, subject, perms }: SubjectFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const [, updateSubject] = useMutation(UpdateSubjectMutationDocument);
  const [, createSubject] = useMutation(CreateSubjectMutationDocument);

  const [{ data }] = useQuery({
    query: SubjectFormPageData,
  });
  const globalSubjects = data?.globalSubjects;

  const formikValues = {
    name: subject?.name ?? '',
    shortName: subject?.shortName ?? '',
    globalSubject: subject?.globalSubject,
    color: subject?.color ?? '',
  };

  const handleSubmit = async (values: typeof formikValues) => {
    if (subject && perms?.canUpdateSubject({ uuid: organizationUuid })) {
      const { error } = await updateSubject({
        update: {
          globalSubject: values.globalSubject,
          name: values.name,
          color: values.color,
          shortName: values.shortName,
        },
        uuid: subject.uuid!,
      });

      error ? showErrorToast(error) : showSuccessToast(t('subjects.saved'));
    }
    if (!subject && perms?.canCreateSubject({ uuid: organizationUuid })) {
      const { error } = await createSubject({
        input: {
          foreignRefId: [],
          globalSubject: values.globalSubject,
          name: values.name,
          shortName: values.shortName,
          color: values.color,
          organization: organizationUuid,
        },
      });

      error ? showErrorToast(error) : showSuccessToast(t('subjects.saved'));
    }

    closeModal();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required.designation')).min(2, t('validation.textTooShort')),
    shortName: Yup.string().required(t('validation.required.shortName')),
  });

  const selectOpts = useCreateSelectOptions(globalSubjects, 'subject', 'subject');

  const ColorPickerData = colorPickerOptions({
    htmlColorsInUse: colorsInUse,
    currentColor: subject?.color ?? '',
    currentColorUsedBy: subject?.name,
    forTypename: 'Subject',
  });

  return (
    <Formik onSubmit={handleSubmit} initialValues={formikValues} validationSchema={validationSchema}>
      {({ resetForm, isSubmitting, errors, handleChange, values, setFieldValue }) => {
        return (
          <Form className={styles.form}>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={10}>
                  <Input
                    label={t('common.designation')}
                    onChange={handleChange}
                    name={'name'}
                    value={values.name}
                    type={'text'}
                    error={errors.name}
                  />
                </GridColumn>
                <GridColumn width={2}>
                  <Input
                    label={t('common.shortName')}
                    onChange={handleChange}
                    name={'shortName'}
                    value={values.shortName}
                    type={'text'}
                    error={errors.shortName}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <Select
                  label={t('subjects.globalSubject')}
                  onChange={(ev) => setFieldValue('globalSubject', (ev as unknown as SelectOptionType).value)}
                  name={'globalSubject'}
                  isSearchable={true}
                  defaultValue={{
                    value: values.globalSubject ?? '',
                    label: values.globalSubject ?? '',
                  }}
                  options={selectOpts}
                  menuPosition='fixed'
                />
              </GridRow>
              <GridRow spacingBottom='s'>
                <ColorPicker
                  name='color'
                  label={t('common.color')}
                  options={ColorPickerData.groupedColorOptions}
                  maxMenuHeight={300}
                  menuPosition='fixed'
                  onChange={async (option) => {
                    await setFieldValue('color', option?.html);
                  }}
                  defaultValue={ColorPickerData.currentColor}
                  error={errors.color as string}
                />
              </GridRow>
            </Grid>
            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  closeModal();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
